import React from 'react';
import { OnboardingTask, CustomerProgress } from '../../types';

interface TaskItemProps {
  task: OnboardingTask;
  progress?: CustomerProgress;
  onStatusChange: (taskId: string, status: CustomerProgress['status']) => void;
}

const TaskItem: React.FC<TaskItemProps> = ({ task, progress, onStatusChange }) => {
  const status = progress?.status || 'pending';
  const isCompleted = status === 'completed';
  const isOptional = task.taskType === 'optional';

  const handleClick = () => {
    if (status === 'pending') {
      onStatusChange(task.id, 'in_progress');
    } else if (status === 'in_progress') {
      onStatusChange(task.id, 'completed');
    }
  };

  const handleSkip = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStatusChange(task.id, 'skipped');
  };

  const getStatusIcon = () => {
    if (isCompleted) return '✅';
    if (status === 'in_progress') return '⏳';
    if (status === 'skipped') return '⏭️';
    return '○';
  };

  const getTaskTypeIcon = () => {
    if (task.taskType === 'required') return '🎯';
    if (task.taskType === 'recommended') return '⭐';
    return '💡';
  };

  return (
    <div 
      className={`task-item-modern ${status} ${task.taskType}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <div className="task-status-indicator">
        <div className={`status-ring ${status}`}>
          <span className="status-icon">{getStatusIcon()}</span>
        </div>
        {status === 'in_progress' && (
          <div className="progress-pulse" />
        )}
      </div>
      
      <div className="task-main-content">
        <div className="task-header">
          <h4 className="task-title-modern">
            {task.title}
          </h4>
          <div className="task-badges">
            <span className={`task-type-badge ${task.taskType}`}>
              <span className="badge-icon">{getTaskTypeIcon()}</span>
              <span className="badge-text">
                {task.taskType === 'required' ? 'Required' : 
                 task.taskType === 'recommended' ? 'Recommended' : 'Optional'}
              </span>
            </span>
          </div>
        </div>
        
        <p className="task-description-modern">{task.description}</p>
        
        {isCompleted && progress?.completedDate && (
          <div className="task-completion-modern">
            <div className="completion-icon">🏆</div>
            <div className="completion-text">
              {progress.updatedByName ? (
                <>
                  <span className="completed-by-name">{progress.updatedByName}</span>
                  <span className="completed-separator">•</span>
                </>
              ) : null}
              <span className="completed-date-modern">
                {new Date(progress.completedDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
          </div>
        )}
        
        {status === 'pending' && (
          <div className="task-cta">
            <span className="cta-text">Click to start →</span>
          </div>
        )}
        
        {status === 'in_progress' && (
          <div className="task-cta in-progress">
            <span className="cta-text">Click to complete ✓</span>
          </div>
        )}
      </div>
      
      {isOptional && status !== 'completed' && status !== 'skipped' && (
        <button 
          className="skip-button-modern" 
          onClick={handleSkip}
          aria-label="Skip this optional task"
        >
          Skip
        </button>
      )}
    </div>
  );
};

export default TaskItem;